import { type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getFormLabel } from '@/shared/copy/forms';
import { getToastMessage } from '@/shared/copy/toasts';
import { Box, DialogFormBody, Text } from '@/ui';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import { TextField } from '@/ui/primitives/TextField';
import { projectSettingsConfirmationCodeSx } from './projectSettingsDestructiveSurfaceSx';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

export type ProjectDestructiveAction = 'deleteData' | 'deleteProject';

export type ProjectDestructiveDialogProps = {
  action: ProjectDestructiveAction;
  project: ProjectDto;
  deleteProject: {
    isPending: boolean;
    mutate: (
      input: { id: string },
      options?: {
        onSuccess?: () => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  wipeProjectData: {
    isPending: boolean;
    mutate: (
      input: {
        id: string;
        confirmation: string;
        reasonCode?: string;
        ticket?: string;
        async?: boolean;
      },
      options?: {
        onSuccess?: (result: {
          status: 'wiped' | 'scheduled';
          jobId?: string;
          jobStatus?: 'pending' | 'running' | 'completed' | 'failed';
          recoveryHint?: string;
        }) => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  onClose: () => void;
  onProjectDeleted: () => void;
};

export const ProjectDestructiveDialog = ({
  action,
  project,
  deleteProject,
  wipeProjectData,
  mutationError,
  notify,
  onClose,
  onProjectDeleted,
}: ProjectDestructiveDialogProps): ReactElement => {
  const confirmationForm = useDialogFormState({ projectName: '' });
  const confirmationErrors = useFormErrors(confirmationForm);
  const expectedProjectName = project.name.trim();
  const projectNameMatches =
    expectedProjectName.length > 0 &&
    confirmationForm.values.projectName.trim() === expectedProjectName;
  const loading = deleteProject.isPending || wipeProjectData.isPending;
  const isDeleteData = action === 'deleteData';

  const confirmExpectedProjectName = (): boolean => {
    const valid = confirmationForm.validateRequired('projectName');
    if (!valid) return false;
    if (confirmationForm.values.projectName.trim() === expectedProjectName) return true;
    confirmationForm.setFieldError('projectName', 'Enter the exact project name to confirm.');
    return false;
  };

  const handleClose = (): void => {
    if (loading) return;
    mutationError.clearError();
    onClose();
  };

  const handleConfirm = (): void => {
    if (!confirmExpectedProjectName()) return;

    if (isDeleteData) {
      wipeProjectData.mutate(
        {
          id: project.id,
          confirmation: `WIPE_PROJECT_DATA:${project.id}`,
          reasonCode: 'USER_REQUEST',
          async: true,
        },
        {
          onSuccess: (result) => {
            mutationError.clearError();
            onClose();
            if (result.status === 'scheduled') {
              notify({
                message: getToastMessage('project.dataWipeScheduled', { name: project.name }),
                tone: 'blue',
              });
              return;
            }
            notify({
              message: getToastMessage('project.dataWiped', { name: project.name }),
              tone: 'green',
            });
          },
          onError: mutationError.setError,
        },
      );
      return;
    }

    deleteProject.mutate(
      { id: project.id },
      {
        onSuccess: () => {
          mutationError.clearError();
          onClose();
          notify({
            message: getToastMessage('entity.deleted', {
              entity: 'project',
              name: project.name,
            }),
            tone: 'green',
          });
          onProjectDeleted();
        },
        onError: mutationError.setError,
      },
    );
  };

  return (
    <ConfirmDialog
      open
      title={
        isDeleteData
          ? getDialogMessage('destructive.deleteNamed.title', { label: 'Project Data' })
          : getDialogMessage('destructive.delete.title', { entity: 'project' })
      }
      description={
        isDeleteData
          ? 'This action is permanent and cannot be undone. It will remove all project data.'
          : 'This action is permanent and cannot be undone.'
      }
      confirmLabel={
        isDeleteData
          ? getDialogMessage('destructive.deleteNamed.confirmLabel', { label: 'Data' })
          : getDialogMessage('destructive.delete.confirmLabel', { entity: 'project' })
      }
      confirmColor="error"
      confirmVariant="contained"
      confirmDisabled={!projectNameMatches}
      loading={loading}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <DialogFormBody>
        <Text role="body" color="textSecondary">
          Enter{' '}
          <Box component="code" sx={projectSettingsConfirmationCodeSx}>
            {expectedProjectName}
          </Box>{' '}
          to confirm.
        </Text>
        <TextField
          label={getFormLabel('projectName')}
          fieldStyle="settings"
          value={confirmationForm.values.projectName}
          onChange={confirmationForm.handleChange('projectName')}
          {...confirmationErrors.fieldErrorProps('projectName')}
          fullWidth
        />
      </DialogFormBody>
    </ConfirmDialog>
  );
};
